import React, { useState, useRef, useEffect } from 'react';
import { FaTimes, FaArrowLeft, FaUserPlus, FaTrash, FaPaperPlane } from 'react-icons/fa';
import LoadingSpinner from './partial/LoadingSpinner.jsx';
import '../styles/BandSettings.css';
import config from '../config.js';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import { useT } from '../contexts/LocaleContext.jsx';
import { BAND_INVITES_ENABLED } from '../config/features.js';

/** Catalog keys for the backend's member roles; a role not listed here is shown as served. */
const ROLE_KEYS = Object.freeze({
  member: 'bandMembers.roles.member',
  moderator: 'bandMembers.roles.moderator',
  admin: 'bandMembers.roles.admin',
});

// The HTTP status of a failed request. fetchWrapper rejects with the status
// on the error itself; reading `error.response.status` (an axios shape) meant
// the invite and remove messages below never matched (ENG-517).
const failureStatus = (error) => error?.status ?? error?.response?.status;

function BandMembers({ band, onBack, onSuccess }) {
  const [members, setMembers] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState({});
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const fetchWrapperRef = useRef(useFetchWrapper());
  const t = useT();

  useEffect(() => {
    if (band && band.Members) {
      setMembers(band.Members || []);
    }
  }, [band]);

  const handleInviteMember = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!inviteEmail.trim()) {
      setError(t('bandMembers.errors.emailRequired'));
      return;
    }

    // Email validation that supports plus addressing and periods
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(inviteEmail)) {
      setError(t('bandMembers.errors.emailInvalid'));
      return;
    }

    // Check if user is already a member
    const isAlreadyMember = members.some(member => 
      member.Email && member.Email.toLowerCase() === inviteEmail.toLowerCase()
    );

    if (isAlreadyMember) {
      setError(t('bandMembers.errors.alreadyMember'));
      return;
    }

    try {
      setInviteLoading(true);
      
      await fetchWrapperRef.current.post(`${config.apiBaseUrl}/bands/${band.ID || band.id}/invite`, {
        email: inviteEmail
      });
      
      setSuccessMessage(t('bandMembers.success.invitationSent', { email: inviteEmail }));
      setInviteEmail('');
      
      // Call onSuccess callback to refresh band data
      if (onSuccess) {
        onSuccess();
      }
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      
    } catch (error) {
      console.error('Error inviting member:', error);
      const status = failureStatus(error);
      if (status === 400) {
        setError(t('bandMembers.errors.inviteInvalid'));
      } else if (status === 409) {
        setError(t('bandMembers.errors.inviteConflict'));
      } else {
        setError(t('bandMembers.errors.inviteFailed'));
      }
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    setError('');
    setSuccessMessage('');

    // Confirm removal
    if (!window.confirm(t('bandMembers.confirm.removeMember', { name: memberName }))) {
      return;
    }

    try {
      setRemoveLoading(prev => ({ ...prev, [memberId]: true }));
      
      await fetchWrapperRef.current.delete(`${config.apiBaseUrl}/bands/${band.ID || band.id}/members/${memberId}`);
      
      setSuccessMessage(t('bandMembers.success.memberRemoved', { name: memberName }));
      
      // Call onSuccess callback to refresh band data
      if (onSuccess) {
        onSuccess();
      }
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      
    } catch (error) {
      console.error('Error removing member:', error);
      const status = failureStatus(error);
      if (status === 403) {
        setError(t('bandMembers.errors.removeForbidden'));
      } else if (status === 400) {
        setError(t('bandMembers.errors.removeOwner'));
      } else {
        setError(t('bandMembers.errors.removeFailed'));
      }
    } finally {
      setRemoveLoading(prev => ({ ...prev, [memberId]: false }));
    }
  };

  const getCurrentUserId = () => {
    return sessionStorage.getItem('userId');
  };

  const isCurrentUser = (member) => {
    const currentUserId = getCurrentUserId();
    return member.UserID && currentUserId && member.UserID.toString() === currentUserId.toString();
  };

  const isBandOwner = (member) => {
    return member.Role === 'owner';
  };

  return (
    <div className="band-settings-content">
      <div className="band-settings-header">
        <button onClick={onBack} className="band-settings-back-button">
          <FaArrowLeft className="rtl-mirror" /> {t('common.actions.back')}
        </button>
        <h2 className="band-settings-title">{t('bandMembers.title', { name: band?.Name || '' })}</h2>
      </div>
      
      <div className="band-settings-content">
        {error && (
          <div className="message error-message" role="alert">
            <FaTimes className="message-icon" />
            {error}
          </div>
        )}
        
        {successMessage && (
          <div className="message success-message" role="status">
            ✓ {successMessage}
          </div>
        )}

        {/* Invite New Member Section: hidden until an invitation can be
            accepted (ENG-518); it comes back with ENG-491. */}
        {BAND_INVITES_ENABLED && (
        <div className="form-section">
          <label className="form-label">
            <FaUserPlus style={{ marginRight: '8px' }} />
            {t('bandMembers.invite.label')}
          </label>
          <form onSubmit={handleInviteMember} className="invite-member-form">
            <div className="invite-input-container">
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="form-input invite-input"
                placeholder={t('bandMembers.invite.placeholder')}
                disabled={inviteLoading}
              />
              {inviteLoading ? (
                <LoadingSpinner />
              ) : (
                <button type="submit" className="invite-button">
                  <FaPaperPlane /> {t('bandMembers.invite.send')}
                </button>
              )}
            </div>
          </form>
        </div>
        )}

        {/* Current Members Section */}
        <div className="form-section">
          <label className="form-label">{t('bandMembers.members.heading', { count: members.length })}</label>
          <div className="members-list">
            {members.length === 0 ? (
              <div className="no-members-message">
                {t('bandMembers.members.empty')}
              </div>
            ) : (
              members.map((member, index) => (
                <div key={member.UserID || index} className="member-item">
                  <div className="member-info">
                    <div className="member-name">
                      {member.Username || member.Name || t('bandMembers.members.unknownUser')}
                      {isBandOwner(member) && (
                        <span className="owner-badge">{t('bandMembers.members.ownerBadge')}</span>
                      )}
                      {isCurrentUser(member) && (
                        <span className="current-user-badge">{t('bandMembers.members.youBadge')}</span>
                      )}
                    </div>
                    {member.Role && member.Role !== 'owner' && (
                      <div className="member-role">
                        {ROLE_KEYS[String(member.Role).toLowerCase()] ? t(ROLE_KEYS[String(member.Role).toLowerCase()]) : member.Role}
                      </div>
                    )}
                  </div>
                  <div className="member-actions">
                    {!isBandOwner(member) && !isCurrentUser(member) && (
                      removeLoading[member.UserID] ? (
                        <LoadingSpinner />
                      ) : (
                        <button
                          onClick={() => handleRemoveMember(member.UserID, member.Username || member.Name)}
                          className="remove-member-button"
                          title={t('bandMembers.members.removeTitle')}
                        >
                          <FaTrash />
                        </button>
                      )
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default BandMembers;